import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Chart, BarElement, ArcElement, CategoryScale, LinearScale,
  Tooltip, Legend, DoughnutController, BarController,
} from 'chart.js';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ChartConfiguration = any;
import { api } from '../api/client';
import KpiCard from '../components/KpiCard';
import TablePagination from '../components/TablePagination';
import { SkuDrawer, Badge } from '../components/SkuDrawer';
import { ABC_COLOR, STATUS_COLOR } from '../lib/colors';
import { fmtMoney, fmtInt, fmtPct, fmtDays } from '../lib/fmt';
import { exportTableAsExcel } from '../lib/export';
import type { ChartDataOut, ItemOut, KPIsOut, PaginatedResponse } from '../types/api';

type DashboardSortField =
  | 'sku' | 'nome' | 'classe' | 'valor_perdido' | 'cobertura_dias'
  | 'status_estoque' | 'sugestao_compra' | 'investimento' | 'margem_pct' | 'workflow_status';

type SortDirection = 'asc' | 'desc';

function exportItemsXLSX(items: ItemOut[]) {
  const headers = ['SKU','Produto','Marca','ABC','Fat. em risco','Cobertura (d)','Status estoque','Sugestão (un)','Investimento','Margem (%)','Status workflow'];
  const rows = items.map(i => [
    i.sku, i.nome, i.marca??'', i.classe,
    Number(i.valor_perdido),
    i.cobertura_dias??'',
    i.status_estoque, i.sugestao_compra,
    i.investimento,
    Number((Number(i.margem_pct)*100).toFixed(1)),
    i.workflow_status,
  ]);
  exportTableAsExcel(`MBC_dashboard_${new Date().toISOString().slice(0,10)}.xlsx`, headers, rows);
}

Chart.register(BarElement, ArcElement, CategoryScale, LinearScale, Tooltip, Legend, DoughnutController, BarController);
Chart.defaults.color = '#8A9BB8';
Chart.defaults.font.family = 'Inter, Segoe UI, system-ui, sans-serif';
Chart.defaults.borderColor = '#22324F';

const PALETTE = ['#F47A20', '#0A4D9E', '#1E6FD6', '#FB9D4B', '#22a06b', '#e0457b', '#9b5de5', '#00b4d8'];


function CanvasChart({ id, cfg }: { id: string; cfg: ChartConfiguration }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const inst = useRef<Chart | null>(null);
  useEffect(() => {
    if (!ref.current) return;
    inst.current?.destroy();
    inst.current = new Chart(ref.current, cfg);
    return () => { inst.current?.destroy(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(cfg.data)]);
  return <canvas ref={ref} id={id} />;
}

export default function Dashboard() {
  const nav = useNavigate();
  const qc  = useQueryClient();
  const [sortBy, setSortBy] = useState<DashboardSortField>('valor_perdido');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');
  const [selectedItem, setSelectedItem] = useState<ItemOut | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  useEffect(() => {
    setPage(1);
  }, [sortBy, sortDir]);

  const kpisQ  = useQuery<KPIsOut>({ queryKey: ['kpis'], queryFn: () => api.get<KPIsOut>('/analysis/kpis').then(r => r.data) });
  const chartQ = useQuery<ChartDataOut>({ queryKey: ['chart-data'], queryFn: () => api.get<ChartDataOut>('/analysis/chart-data').then(r => r.data) });
  const itemsQ = useQuery<PaginatedResponse<ItemOut>>({
    queryKey: ['items-ceo', sortBy, sortDir, page],
    queryFn: () =>
      api
        .get<PaginatedResponse<ItemOut>>('/analysis/items', {
          params: { limit: pageSize, offset: (page - 1) * pageSize, sort_by: sortBy, sort_dir: sortDir },
        })
        .then(r => r.data),
  });

  const k = kpisQ.data;
  const c = chartQ.data;
  const items = itemsQ.data?.items ?? [];
  const totalItems = itemsQ.data?.total ?? 0;

  const topRuptCfg: ChartConfiguration | null = c?.top_ruptura?.length ? {
    type: 'bar',
    data: {
      labels: c.top_ruptura.map(i => i.sku),
      datasets: [{ label: 'R$ em risco', data: c.top_ruptura.map(i => Math.round(i.valor_perdido)), backgroundColor: '#F47A20', borderRadius: 6 }],
    },
    options: {
      plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx: any) => fmtMoney(ctx.raw as number), afterLabel: (ctx: any) => c.top_ruptura[ctx.dataIndex]?.nome?.slice(0, 40) ?? '' } } },
      scales: { y: { ticks: { callback: (v: any) => `R$${(Number(v) / 1000).toFixed(0)}k` } } },
    },
  } : null;

  const saudeCfg: ChartConfiguration | null = c ? {
    type: 'doughnut',
    data: {
      labels: ['Saudável / OK', 'Crítico / Zerado', 'Excesso'],
      datasets: [{ data: [(c.saude_estoque['Saudável'] ?? 0) + (c.saude_estoque['Sem venda'] ?? 0), (c.saude_estoque['Crítico'] ?? 0) + (c.saude_estoque['Zerado'] ?? 0), c.saude_estoque['Excesso'] ?? 0], backgroundColor: ['#22a06b', '#e0457b', '#1E6FD6'], borderWidth: 0 }],
    },
    options: { cutout: '62%', plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, padding: 12 } } } },
  } : null;

  const abcKeys = Object.keys(c?.abc_investimento ?? {}).sort();
  const abcCfg: ChartConfiguration | null = c ? {
    type: 'bar',
    data: { labels: abcKeys, datasets: [{ data: abcKeys.map(k2 => c.abc_investimento[k2] ?? 0), backgroundColor: PALETTE, borderRadius: 6 }] },
    options: { plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx: any) => fmtMoney(ctx.raw as number) } } }, scales: { y: { ticks: { callback: (v: any) => `R$${(Number(v) / 1000).toFixed(0)}k` } } } },
  } : null;

  const marcaCfg: ChartConfiguration | null = c?.top_marcas?.length ? {
    type: 'bar',
    data: { labels: c.top_marcas.map(m => m.marca), datasets: [{ data: c.top_marcas.map(m => m.valor), backgroundColor: '#0A4D9E', borderRadius: 6 }] },
    options: { indexAxis: 'y' as const, plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx: any) => fmtMoney(ctx.raw as number) } } }, scales: { x: { ticks: { callback: (v: any) => `R$${(Number(v) / 1000).toFixed(0)}k` } } } },
  } : null;

  const margemKeys = ['Aprovado', 'Negociar', 'Bloqueado', 'Sem dado'];
  const margemCfg: ChartConfiguration | null = c ? {
    type: 'doughnut',
    data: { labels: margemKeys, datasets: [{ data: margemKeys.map(k2 => c.margem_classificacao[k2] ?? 0), backgroundColor: ['#22a06b', '#FB9D4B', '#e0457b', '#8A9BB8'], borderWidth: 0 }] },
    options: { cutout: '60%', plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, padding: 10 } } } },
  } : null;

  if (kpisQ.isLoading) return <div className="text-mbc-mute text-center mt-10">Carregando…</div>;
  if (!k) return <div className="text-mbc-mute text-center mt-10">Nenhum dado processado. Vá para Importação.</div>;

  const onSort = (field: DashboardSortField) => {
    if (sortBy === field) {
      setSortDir(current => (current === 'desc' ? 'asc' : 'desc'));
      return;
    }
    setSortBy(field);
    setSortDir(field === 'nome' || field === 'sku' || field === 'classe' || field === 'status_estoque' || field === 'workflow_status' ? 'asc' : 'desc');
  };

  const sortIcon = (field: DashboardSortField) => {
    if (sortBy !== field) return '↕';
    return sortDir === 'desc' ? '↓' : '↑';
  };

  return (
    <>
      <div className="space-y-6 fade-in">

      {/* 8 KPIs */}
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard label="Compra Sugerida (R$)" value={fmtMoney(k.valor_sugestao)} sub={`${fmtInt(k.aprovados)} SKUs a repor`}    accent="mbc-orangel" icon="🛒" />
        <KpiCard label="Faturamento em Risco"  value={fmtMoney(k.valor_perdido)}  sub="ruptura projetada"                         accent="rose-300"    icon="⚠️" />
        <KpiCard label="Margem Média"          value={fmtPct(k.margem_media)}     sub="mín. aceitável 10%"                       accent={k.margem_media >= 0.10 ? 'emerald-300' : 'amber-300'} icon="📊" />
        <KpiCard label="Cobertura Média"       value={fmtDays(k.cobertura_media)} sub="dias de estoque"                         accent="sky-300"     icon="📦" />
        <KpiCard label="Itens Zerados"         value={fmtInt(k.zerados)}          sub="sem estoque c/ venda"                    accent="rose-300"    icon="🔴" />
        <KpiCard label="Itens Críticos"        value={fmtInt(k.criticos)}         sub="risco de ruptura"                        accent="amber-300"   icon="🟠" />
        <KpiCard label="Aguardando Aprovação"  value={fmtInt(k.aguard_aprov)}     sub="no workflow"                             accent="mbc-bluel"   icon="⏳" />
        <KpiCard label="Bloqueados (margem)"   value={fmtInt(k.bloqueados)}       sub="não comprar no custo atual"              accent="rose-300"    icon="⛔" />
      </div>

      {/* Gráficos linha 1 */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-white font-bold">Top rupturas por faturamento em risco</h3>
            <span className="text-xs text-mbc-mute">R$ potencial perdido</span>
          </div>
          {topRuptCfg ? <CanvasChart id="cTopRupt" cfg={topRuptCfg} /> : <p className="text-mbc-mute text-sm py-8 text-center">Sem rupturas</p>}
        </div>
        <div className="card p-5">
          <h3 className="text-white font-bold mb-2">Saúde do estoque</h3>
          {saudeCfg && <CanvasChart id="cSaude" cfg={saudeCfg} />}
        </div>
      </div>

      {/* Gráficos linha 2 */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="card p-5">
          <h3 className="text-white font-bold mb-2">Investimento por curva ABC</h3>
          {abcCfg ? <CanvasChart id="cABC" cfg={abcCfg} /> : <p className="text-mbc-mute text-sm py-8 text-center">Sem sugestões</p>}
        </div>
        <div className="card p-5">
          <h3 className="text-white font-bold mb-2">Top marcas — compra sugerida</h3>
          {marcaCfg ? <CanvasChart id="cMarca" cfg={marcaCfg} /> : <p className="text-mbc-mute text-sm py-8 text-center">Sem dados</p>}
        </div>
        <div className="card p-5">
          <h3 className="text-white font-bold mb-2">Classificação de margem</h3>
          {margemCfg && <CanvasChart id="cMargem" cfg={margemCfg} />}
        </div>
      </div>

      {/* Tabela */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-white font-bold">Produtos de maior impacto financeiro</h3>
          <div className="flex gap-2">
            {items.length > 0 && (
              <button onClick={() => exportItemsXLSX(items)} className="text-xs bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 px-3 py-1.5 rounded-lg transition-colors">
                📥 Exportar Excel
              </button>
            )}
            <button onClick={() => nav('/comprador')} className="text-xs bg-mbc-orange/20 text-mbc-orangel px-3 py-1.5 rounded-lg hover:bg-mbc-orange/30">
              Abrir painel do comprador →
            </button>
          </div>
        </div>
        <div className="scroller">
          <table className="dt">
            <thead>
              <tr>
                <th style={{ width: 90 }} onClick={() => onSort('sku')}>SKU {sortIcon('sku')}</th>
                <th className="text-left" style={{ width: 300 }} onClick={() => onSort('nome')}>Produto {sortIcon('nome')}</th>
                <th className="text-center" style={{ width: 56 }} onClick={() => onSort('classe')}>ABC {sortIcon('classe')}</th>
                <th className="num" style={{ width: 110 }} onClick={() => onSort('valor_perdido')}>Fat. risco {sortIcon('valor_perdido')}</th>
                <th className="num" style={{ width: 95 }} onClick={() => onSort('cobertura_dias')}>Cobertura {sortIcon('cobertura_dias')}</th>
                <th className="text-center" style={{ width: 100 }} onClick={() => onSort('status_estoque')}>Estoque {sortIcon('status_estoque')}</th>
                <th className="num" style={{ width: 90 }} onClick={() => onSort('sugestao_compra')}>Sugestão {sortIcon('sugestao_compra')}</th>
                <th className="num" style={{ width: 120 }} onClick={() => onSort('investimento')}>Investimento {sortIcon('investimento')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map(i => (
                <tr
                  key={i.sku}
                  className="cursor-pointer"
                  onClick={() => setSelectedItem(i)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setSelectedItem(i);
                    }
                  }}
                  tabIndex={0}
                >
                  <td><span className="font-mono text-mbc-bluel text-xs">{i.sku}</span></td>
                  <td><span className="truncate inline-block max-w-[280px]" title={i.nome}>{i.nome}</span></td>
                  <td className="text-center"><Badge txt={i.classe} map={ABC_COLOR} /></td>
                  <td className="num">{fmtMoney(Number(i.valor_perdido))}</td>
                  <td className="num">{fmtDays(i.cobertura_dias)}</td>
                  <td className="text-center"><Badge txt={i.status_estoque} map={STATUS_COLOR} /></td>
                  <td className="num">{fmtInt(i.sugestao_compra)}</td>
                  <td className="num">{fmtMoney(i.investimento)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <TablePagination total={totalItems} page={page} pageSize={pageSize} onPageChange={setPage} />
      </div>

      </div>
      <SkuDrawer
        item={selectedItem}
        onClose={() => setSelectedItem(null)}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ['items-ceo'] });
          qc.invalidateQueries({ queryKey: ['kpis'] });
        }}
      />
    </>
  );
}
